import { useEffect, useState } from "react";
import { Save, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const PAGES = [["privacy", "Privacy Notice"], ["terms", "Terms of Use"], ["contributor", "Contributor Agreement"]];

export const ReaderSwitches = ({ f, set }) => (
  <section data-testid="settings-readers-section" className="vellum p-6 grid gap-5">
    <p className="eyebrow">Readers, newsletter & legal pages</p>
    <label className="flex items-start gap-4">
      <Switch data-testid="settings-readers-toggle" checked={!!f.readers_enabled} onCheckedChange={(v) => set({ readers_enabled: v })} />
      <span><b className="font-heading">Reader contributions</b><span className="block text-sm italic text-[#4a3a2a]">When off, the Reader Contribution button, reader sign-up and reader login disappear, and new submissions are refused. Approved reader articles stay published. Your master reader account always works.</span></span>
    </label>
    <label className="flex items-start gap-4">
      <Switch data-testid="settings-legal-toggle" checked={!!f.legal_enabled} onCheckedChange={(v) => set({ legal_enabled: v })} />
      <span><b className="font-heading">Show legal pages</b><span className="block text-sm italic text-[#4a3a2a]">Adds Privacy Notice, Terms of Use and Contributor Agreement links to the footer, and asks new readers to accept them when signing up.</span></span>
    </label>
    <label className="flex items-start gap-4">
      <Switch data-testid="settings-newsletter-toggle" checked={f.newsletter_enabled !== false} onCheckedChange={(v) => set({ newsletter_enabled: v })} />
      <span><b className="font-heading">Newsletter sign-up box</b><span className="block text-sm italic text-[#4a3a2a]">Shows the book release sign-up box at the bottom of the home page and in the footer. Change its wording on the Site Wording page.</span></span>
    </label>
    <p className="text-xs italic text-[#991b1b]">Remember to click Save at the top to apply these switches.</p>
  </section>
);

export const LegalEditor = () => {
  const [pages, setPages] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/legal").then((r) => setPages(r.data)); }, []);
  if (!pages) return null;
  const set = (k, p) => setPages((x) => ({ ...x, [k]: { ...x[k], ...p } }));
  const save = async () => {
    setBusy(true);
    try { setPages((await api.put("/admin/legal", { pages })).data); toast.success("Legal pages saved"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section data-testid="legal-editor" className="vellum p-6 grid gap-6 mt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><p className="eyebrow">Legal pages</p><p className="text-sm italic text-[#4a3a2a] max-w-2xl">Drafts are templates, not legal advice. Have a lawyer review them and replace every [bracketed] item before switching the pages on. Use "## " at the start of a line for a heading, "- " for bullet points, and a blank line between paragraphs.</p></div>
        <button data-testid="legal-save" onClick={save} disabled={busy} className="btn-illum theme-flame disabled:opacity-60"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save legal pages"}</button>
      </div>
      {PAGES.map(([k, label]) => (
        <div key={k} className="grid gap-2">
          <div className="flex items-center justify-between gap-3">
            <input data-testid={`legal-title-${k}`} value={pages[k].title} onChange={(e) => set(k, { title: e.target.value })} className="admin-input font-heading font-bold !w-auto flex-1" aria-label={`${label} title`} />
            <a href={`/legal/${k}`} target="_blank" rel="noopener noreferrer" data-testid={`legal-view-${k}`} className="text-xs font-heading inline-flex items-center gap-1 hover:text-[#991b1b]"><ExternalLink className="w-3 h-3" /> View</a>
          </div>
          <textarea data-testid={`legal-body-${k}`} rows={12} value={pages[k].body} onChange={(e) => set(k, { body: e.target.value })} className="admin-input font-mono text-sm" />
        </div>
      ))}
    </section>
  );
};
